import { ActionIcon, Box, CopyButton, Flex, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import { LoadingAnimation } from '../chat/components/LoadingAnimation';
import { camelCaseOrSnakeCaseToWords } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { VerifyResult } from '../chat/verify/VerifyResult';
import { VerifyEvidence, VerifyOutcome } from '../chat/verify/types';
import { ThinkingTile } from './ThinkingTile';

type Props = {
  content: string;
  thinkingContent?: string;
  isThinking?: boolean;
  currentState?: string;
  currentToolName?: string;
  retryNotice?: string;
  canCopy?: boolean;
  dark?: boolean;
  verifyOutcome?: VerifyOutcome;
  evidence?: VerifyEvidence[];
  verifyMeetingNoteId?: string;
  verifyMessage?: string;
  verifyHeadline?: string;
  verifyUnresolved?: boolean;
  onVerifyAction?: (outcome: VerifyOutcome, finding?: string) => void;
};

export function SiestaMessage({
  content,
  thinkingContent,
  isThinking,
  currentState,
  currentToolName,
  retryNotice,
  canCopy,
  dark,
  verifyOutcome,
  evidence,
  verifyMeetingNoteId,
  verifyMessage,
  verifyHeadline,
  verifyUnresolved,
  onVerifyAction,
}: Props) {
  const statusLabel =
    currentToolName || (currentState && camelCaseOrSnakeCaseToWords(currentState));

  return (
    <Box>
      {retryNotice ? (
        <Flex justify="start" align="center" gap="xs" mb="xs">
          <LoadingAnimation />
          <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="orange.7">
            {retryNotice}
          </Text>
        </Flex>
      ) : (
        isThinking &&
        statusLabel && (
          <>
            <Flex justify="start" align="center" mb="xs">
              <LoadingAnimation />
            </Flex>
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="orange.7">
              {currentToolName || `${statusLabel}...`}
            </Text>
          </>
        )
      )}
      {thinkingContent && <ThinkingTile content={thinkingContent} />}
      {verifyOutcome ? (
        <VerifyResult
          outcome={verifyOutcome}
          evidence={evidence ?? []}
          meetingNoteId={verifyMeetingNoteId}
          message={verifyMessage}
          headline={verifyHeadline}
          unresolved={verifyUnresolved}
          dark={dark}
          onAction={() => onVerifyAction?.(verifyOutcome, verifyMessage)}
        />
      ) : (
        <>
          <Box
            fz="var(--mantine-font-size-textSm)"
            fw={FONT_WEIGHTS.medium}
            c={dark ? 'white' : 'gray.8'}
          >
            <MarkdownView value={content} />
          </Box>
          {canCopy && content.trim() && (
            <CopyButton value={content} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'} position="top" withArrow fz="xs">
                  <ActionIcon
                    variant="subtle"
                    color={dark ? 'gray.4' : 'gray.6'}
                    size="sm"
                    mt="xxs"
                    aria-label="Copy answer"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
        </>
      )}
    </Box>
  );
}
